import React, { useEffect, useState } from 'react';
import axios from 'axios';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import './GiftedPage.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSocket } from '../../context/SocketContext';

const GiftedPage = () => {
  const location = useLocation();
  const defaultTab = location.state?.tab === 'received' ? 'received' : 'sent';
  const [activeTab, setActiveTab] = useState(defaultTab);
  const { token, profile } = useAuth();
  const [sentGifts, setSentGifts] = useState([]);
  const [receivedGifts, setReceivedGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const navigate = useNavigate();
  const { socket } = useSocket();

  useEffect(() => {
    const fetchGifts = async () => {
      try {
        const [sentRes, receivedRes] = await Promise.all([
          axios.get(api.getGiftsBySender(profile.email), {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(api.getGiftsByRecipient(profile.email), {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setSentGifts(sentRes.data.gifts || []);
        setReceivedGifts(receivedRes.data.gifts || []);
      } catch (err) {
        console.error('Error fetching gifts:', err);
      } finally {
        setLoading(false);
      }
    };

    if (profile?.email) {
      fetchGifts();
    }
  }, [profile?.email, token]);

  useEffect(() => {
    if (!socket) return;
    // Listen for real-time gift status updates
    const handleGiftStatusUpdated = (data) => {
      // data: { giftId, status }
      setSentGifts((prev) =>
        prev.map((gift) =>
          gift._id === data.giftId ? { ...gift, status: data.status } : gift,
        ),
      );
      setReceivedGifts((prev) =>
        prev.map((gift) =>
          gift._id === data.giftId ? { ...gift, status: data.status } : gift,
        ),
      );
    };
    socket.on('giftStatusUpdated', handleGiftStatusUpdated);
    return () => {
      socket.off('giftStatusUpdated', handleGiftStatusUpdated);
    };
  }, [socket]);

  const handleReceiveGift = async (giftId) => {
    try {
      await axios.patch(
        api.updateGiftStatus(giftId),
        { status: 'received' },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      toast.success('Gift received! Redirecting to your learning...');

      setTimeout(() => {
        navigate('/learning');
      }, 2000);
    } catch (error) {
      console.error('Failed to mark gift as received:', error);
      toast.error('Failed to receive the gift.');
    }
  };

  const getPaginatedGifts = (gifts) => {
    const sorted = [...gifts].sort((a, b) => new Date(b.date) - new Date(a.date));
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sorted.slice(startIndex, startIndex + itemsPerPage);
  };

  const totalPages = Math.ceil(
    (activeTab === 'sent' ? sentGifts : receivedGifts).length / itemsPerPage,
  );

  const renderGifts = (gifts) => {
    if (gifts.length === 0) {
      return <p className="no-gifts">No {activeTab} gifts found.</p>;
    }

    const paginated = getPaginatedGifts(gifts);

    return (
      <>
        <table className="gift-table">
          <thead>
            <tr>
              <th>Course</th>
              <th>{activeTab === 'sent' ? 'To' : 'From'}</th>
              <th>Message</th>
              <th>Status</th>
              <th>Date</th>
              {activeTab === 'received' && <th>Action</th>}
            </tr>
          </thead>
          <tbody>
            {paginated.map((gift) => (
              <tr key={gift._id}>
                <td>{gift.courseTitle}</td>
                <td>
                  {activeTab === 'sent' ? gift.recipientEmail : gift.senderEmail}
                </td>
                <td>{gift.giftMessage || '-'}</td>
                <td>
                  <span
                    className={`gift-status-label ${
                      gift.status === 'received' ? 'status-received' : 'status-pending'
                    }`}
                  >
                    {gift.status}
                  </span>
                </td>

                <td>{new Date(gift.date).toLocaleString()}</td>
                {activeTab === 'received' && (
                  <td>
                    {gift.status !== 'received' ? (
                      <button
                        onClick={() => handleReceiveGift(gift._id)}
                        className="receive-gift-button"
                      >
      Receive Gift
                      </button>
                    ) : (
                      <span className="received-label" title="Gift already received">Received Gift</span>
                    )}
                  </td>

                )}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="pagination">
          <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1}>
            Prev
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>
            Next
          </button>
        </div>
      </>
    );
  };

  return (
    <div className="gift-management-modal">
      <ToastContainer position="top-center" autoClose={2000} />
      <button className="gift-close-button" onClick={() => navigate('/home')} title="Close">
  ✖
      </button>

      <h2>Gift Management</h2>
      <div className="tabs">
        <button
          className={activeTab === 'sent' ? 'active' : ''}
          onClick={() => {
            setActiveTab('sent');
            setCurrentPage(1);
          }}
        >
          Sent Gifts
        </button>
        <button
          className={activeTab === 'received' ? 'active' : ''}
          onClick={() => {
            setActiveTab('received');
            setCurrentPage(1);
          }}
        >
          Received Gifts
        </button>
      </div>
      <div className="gift-list">
        {loading ? (
          <p>Loading...</p>
        ) : (
          renderGifts(activeTab === 'sent' ? sentGifts : receivedGifts)
        )}
      </div>
    </div>
  );
};

export default GiftedPage;
